// /admin/features — org-scoped feature flags for LLM-backed integrations (T190).
//
// Lists the two toggleable features (AI Remediation + Copilot Test Generation)
// as cards. Each card shows: title, description, a toggle switch, and the
// "last changed by ... on ..." footer. Requires org_admin role.
//
// Backend: GET /api/v1/admin/features, PATCH /api/v1/admin/features.

import { useState } from 'react';
import useSWR, { mutate } from 'swr';
import { Bot, FlaskConical, Loader2, ToggleLeft, ToggleRight } from 'lucide-react';
import { fetcher, apiRequest } from '../lib/api';
import { PageHeader } from '../components/ui';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FeatureFlag {
  key: string;
  enabled: boolean;
  updated_at: string;
  updated_by?: string;
}

interface FeaturesResponse {
  flags: FeatureFlag[];
}

// ---------------------------------------------------------------------------
// Feature metadata — human-readable info for each well-known flag key.
// ---------------------------------------------------------------------------

interface FeatureMeta {
  label: string;
  description: string;
  credentialsNeeded: string;
  Icon: typeof Bot;
}

const FEATURE_META: Record<string, FeatureMeta> = {
  ai_remediation: {
    label: 'AI Remediation',
    description:
      'Enables the "Suggest fix" button on the Issue Detail drawer. When a developer ' +
      'clicks it, orbital-lint calls the Anthropic Claude API to generate a unified diff ' +
      'that addresses the flagged issue. Each call consumes tokens billed to the Anthropic ' +
      'account whose key is configured in ORBITALLINT_LLM_API_KEY (or in Admin → ' +
      'Settings → LLM).',
    credentialsNeeded: 'Anthropic API key (ORBITALLINT_LLM_API_KEY or per-org LLM settings)',
    Icon: Bot,
  },
  copilot_test_generation: {
    label: 'Copilot Test Generation',
    description:
      'Enables the "@orbital test-this" command on pull-request comments. When a reviewer ' +
      'posts a comment containing "@orbital test-this", orbital-lint replies with a ' +
      'suggested unit test targeting the flagged code smell. Requires the GitHub App ' +
      'to have "issue_comment" event subscriptions and a Copilot Workspace bot token.',
    credentialsNeeded:
      'GitHub App with pull_requests:write scope + ORBITALLINT_COPILOT_TOKEN',
    Icon: FlaskConical,
  },
};

// Fallback for unknown keys.
function metaFor(key: string): FeatureMeta {
  return (
    FEATURE_META[key] ?? {
      label: key,
      description: 'No description available.',
      credentialsNeeded: 'Unknown',
      Icon: Bot,
    }
  );
}

function fmtDate(iso: string): string {
  if (!iso || iso.startsWith('0001')) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// ---------------------------------------------------------------------------
// SWR key
// ---------------------------------------------------------------------------

const FEATURES_KEY = '/api/v1/admin/features';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AdminFeatures() {
  const { data, error, isLoading } = useSWR<FeaturesResponse>(FEATURES_KEY, fetcher);
  const [toggling, setToggling] = useState<string | null>(null);
  const [patchError, setPatchError] = useState<string | null>(null);

  async function handleToggle(flag: FeatureFlag) {
    setToggling(flag.key);
    setPatchError(null);
    try {
      await apiRequest<FeaturesResponse>(FEATURES_KEY, {
        method: 'PATCH',
        body: JSON.stringify({ key: flag.key, enabled: !flag.enabled }),
      });
      await mutate(FEATURES_KEY);
    } catch (e) {
      setPatchError(e instanceof Error ? e.message : 'Failed to update feature flag');
    } finally {
      setToggling(null);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-fg-subtle" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <p className="text-sm text-danger">Failed to load feature flags: {error.message}</p>
      </div>
    );
  }

  const flags = data?.flags ?? [];

  return (
    <div className="p-6 max-w-3xl space-y-6">
      <PageHeader
        title="Features"
        description={
          <>
            Toggle LLM-backed integrations on or off for this organisation. All features default
            to <strong>off</strong> to prevent unexpected API costs on fresh installs.
          </>
        }
      />

      {patchError && (
        <div className="rounded-md border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
          {patchError}
        </div>
      )}

      <div className="space-y-4">
        {flags.map((flag) => {
          const meta = metaFor(flag.key);
          const { Icon } = meta;
          const isToggling = toggling === flag.key;

          let toggleIcon;
          if (isToggling) {
            toggleIcon = <Loader2 className="h-5 w-5 animate-spin" />;
          } else if (flag.enabled) {
            toggleIcon = <ToggleRight className="h-6 w-6 text-accent" />;
          } else {
            toggleIcon = <ToggleLeft className="h-6 w-6" />;
          }

          return (
            <div
              key={flag.key}
              className="rounded-lg border border-border bg-bg-elevated p-5 flex items-start gap-4"
            >
              <div className="mt-0.5 shrink-0">
                <Icon className="h-5 w-5 text-accent" aria-hidden="true" />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-4">
                  <h2 className="text-sm font-semibold text-fg">{meta.label}</h2>
                  <button
                    onClick={() => handleToggle(flag)}
                    disabled={isToggling}
                    aria-label={`${flag.enabled ? 'Disable' : 'Enable'} ${meta.label}`}
                    aria-pressed={flag.enabled}
                    className="shrink-0 text-fg-muted hover:text-accent transition-colors disabled:opacity-50"
                  >
                    {toggleIcon}
                  </button>
                </div>

                <p className="mt-1 text-sm text-fg-subtle leading-relaxed">
                  {meta.description}
                </p>

                <p className="mt-2 text-xs text-fg-subtle">
                  <span className="font-medium text-fg-muted">Credentials required:</span>{' '}
                  {meta.credentialsNeeded}
                </p>

                <p className="mt-2 text-xs text-fg-subtle">
                  {flag.enabled ? (
                    <span className="inline-flex items-center gap-1 text-success">
                      <span className="h-1.5 w-1.5 rounded-full bg-success" />
                      {'Enabled'}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-fg-subtle">
                      <span className="h-1.5 w-1.5 rounded-full bg-fg-subtle" />
                      {'Disabled'}
                    </span>
                  )}
                  {flag.updated_by && (
                    <span className="ml-3">
                      Last changed{flag.updated_by ? ` by ${flag.updated_by}` : ''} on{' '}
                      {fmtDate(flag.updated_at)}
                    </span>
                  )}
                </p>
              </div>
            </div>
          );
        })}

        {flags.length === 0 && (
          <p className="text-sm text-fg-subtle">No feature flags configured.</p>
        )}
      </div>
    </div>
  );
}
